/**
 * Team Chat Realtime Service v2
 *
 * Servizio singleton per gestire la connessione realtime Supabase
 * alla chat del team con supporto multi-conversazione.
 */

import { supabase } from '../supabase';
import { useTeamChatStore, TeamChatMessage, TeamChat } from '../stores/teamChatStore';
import { userProfileCache } from '../stores/referenceDataCache';

class TeamChatRealtimeService {
  private messagesChannel: ReturnType<typeof supabase.channel> | null = null;
  private chatsChannel: ReturnType<typeof supabase.channel> | null = null;
  private typingChannel: ReturnType<typeof supabase.channel> | null = null;
  private reactionsChannel: ReturnType<typeof supabase.channel> | null = null;
  private isInitialized = false;
  private retryCount = 0;
  private maxRetries = 5;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnecting = false;
  private userId: string | null = null;
  private organizationId: string | null = null;

  /**
   * Inizializza il servizio e sottoscrive agli aggiornamenti
   */
  async init(userId: string) {
    console.log('[TeamChatRealtimeService] init() chiamato');

    if (this.isInitialized && this.userId === userId) {
      console.log('[TeamChatRealtimeService] Già inizializzato, skip');
      return;
    }

    if (this.isInitialized && this.userId !== userId) {
      console.log('[TeamChatRealtimeService] User cambiato, disconnetto...');
      await this.disconnect();
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('[TeamChatRealtimeService] Nessuna sessione, skip init');
      return;
    }

    // Recupera organization_id per il canale typing (cache condivisa)
    this.organizationId = await userProfileCache.getOrganizationId(userId).catch(() => null);

    this.userId = userId;
    this.isInitialized = true;
    await this.connect();
  }

  /**
   * Crea e configura i canali Supabase
   */
  private async connect() {
    // Guard: prevent connect if not initialized or already connecting
    if (!this.isInitialized) {
      console.log('[TeamChatRealtimeService] Not initialized, skipping connect');
      return;
    }

    try {
      // Cleanup canali precedenti
      if (this.messagesChannel) {
        await supabase.removeChannel(this.messagesChannel);
        this.messagesChannel = null;
      }
      if (this.chatsChannel) {
        await supabase.removeChannel(this.chatsChannel);
        this.chatsChannel = null;
      }
      if (this.typingChannel) {
        await supabase.removeChannel(this.typingChannel);
        this.typingChannel = null;
      }
      if (this.reactionsChannel) {
        await supabase.removeChannel(this.reactionsChannel);
        this.reactionsChannel = null;
      }

      // Re-check after async cleanup (state might have changed)
      if (!this.isInitialized || !this.userId) {
        console.warn('[TeamChatRealtimeService] State changed during cleanup, aborting');
        this.reconnecting = false;
        return;
      }

      // Channel per messaggi
      const messagesChannelId = `team_chat_messages_${this.userId}_${Date.now()}`;
      this.messagesChannel = supabase.channel(messagesChannelId);

      this.messagesChannel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'team_chat_messages'
          },
          (payload) => this.handleMessageInsert(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'team_chat_messages'
          },
          (payload) => this.handleMessageUpdate(payload)
        )
        .subscribe((status: string, err?: Error) => {
          this.handleSubscriptionStatus('messages', status, err);
        });

      // Channel per nuove chat
      const chatsChannelId = `team_chats_${this.userId}_${Date.now()}`;
      this.chatsChannel = supabase.channel(chatsChannelId);

      this.chatsChannel
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'team_chats'
          },
          (payload) => this.handleChatInsert(payload)
        )
        .subscribe((status: string, err?: Error) => {
          this.handleSubscriptionStatus('chats', status, err);
        });

      // Canale Broadcast per typing indicator
      if (this.organizationId) {
        const typingChannelId = `team_chat_typing_${this.organizationId}`;
        this.typingChannel = supabase.channel(typingChannelId);
        this.typingChannel
          .on('broadcast', { event: 'typing' }, (payload) => {
            if (!this.isInitialized || !this.userId) return;
            const { chat_id, user_id, user_name } = payload.payload as { chat_id: string; user_id: string; user_name: string };
            if (user_id === this.userId) return; // Ignora il proprio typing
            useTeamChatStore.getState().setUserTyping(chat_id, user_id, user_name);
          })
          .subscribe();
      }

      // Channel per reazioni ai messaggi
      const reactionsChannelId = `team_chat_reactions_${this.userId}_${Date.now()}`;
      this.reactionsChannel = supabase.channel(reactionsChannelId);
      this.reactionsChannel
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'team_chat_message_reactions' },
          (payload) => this.handleReactionChange(payload.new as { message_id: string; user_id: string; emoji: string }, 'add'))
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'team_chat_message_reactions' },
          (payload) => this.handleReactionChange(payload.old as { message_id: string; user_id: string; emoji: string }, 'remove'))
        .subscribe();

    } catch (error) {
      console.error('[TeamChatRealtimeService] Errore connessione:', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Handler per reazioni (INSERT/DELETE su team_chat_message_reactions)
   */
  private handleReactionChange(data: { message_id: string; user_id: string; emoji: string }, type: 'add' | 'remove') {
    if (!this.isInitialized || !this.userId) return;
    if (data.user_id === this.userId) return; // Skip propria reazione (già ottimistica)

    const store = useTeamChatStore.getState();
    // Trova chatId cercando il messageId nelle cache locali
    const chatId = Object.keys(store.messages).find(cId =>
      store.messages[cId].some(m => m.id === data.message_id)
    );
    if (!chatId) return;

    if (type === 'add') {
      store.addReactionFromRealtime(data.message_id, chatId, data.emoji, data.user_id);
    } else {
      store.removeReactionFromRealtime(data.message_id, chatId, data.emoji, data.user_id);
    }
  }

  /**
   * Invia evento typing via Broadcast
   */
  sendTyping(chatId: string, userId: string, userName: string) {
    if (!this.typingChannel || !this.isInitialized) return;
    this.typingChannel.send({
      type: 'broadcast',
      event: 'typing',
      payload: { chat_id: chatId, user_id: userId, user_name: userName }
    });
  }

  /**
   * Handler per nuovi messaggi (INSERT)
   */
  private handleMessageInsert(payload: { new: Record<string, unknown> }) {
    // Guard: skip if service has been disconnected
    if (!this.isInitialized || !this.userId) return;

    const newMessage = payload.new as unknown as TeamChatMessage;
    console.log('[TeamChatRealtimeService] Nuovo messaggio:', newMessage.id);

    // Verifica se l'utente è partecipante di questa chat
    // (la RLS dovrebbe già filtrare, ma verifichiamo)
    useTeamChatStore.getState().addMessageFromRealtime(newMessage);
  }

  /**
   * Handler per messaggi aggiornati (UPDATE - edit/delete)
   */
  private handleMessageUpdate(payload: { new: Record<string, unknown> }) {
    // Guard: skip if service has been disconnected
    if (!this.isInitialized || !this.userId) return;

    const updatedMessage = payload.new as unknown as TeamChatMessage;
    console.log('[TeamChatRealtimeService] Messaggio aggiornato:', updatedMessage.id);

    useTeamChatStore.getState().updateMessageFromRealtime(updatedMessage);
  }

  /**
   * Handler per nuove chat (INSERT)
   */
  private async handleChatInsert(payload: { new: Record<string, unknown> }) {
    // Guard: skip if service has been disconnected
    if (!this.isInitialized || !this.userId) return;

    const newChat = payload.new as unknown as TeamChat;
    const currentUserId = this.userId; // Capture before async
    console.log('[TeamChatRealtimeService] Nuova chat:', newChat.id);

    try {
      // Verifica se l'utente è partecipante
      const { data, error } = await supabase
        .from('team_chat_participants')
        .select('id')
        .eq('chat_id', newChat.id)
        .eq('user_id', currentUserId)
        .single();

      // Re-check state after async operation
      if (!this.isInitialized || this.userId !== currentUserId) {
        console.log('[TeamChatRealtimeService] State changed during handleChatInsert, aborting');
        return;
      }

      if (error) {
        // PGRST116 = no rows returned, which is fine (user is not a participant)
        if (error.code !== 'PGRST116') {
          console.error('[TeamChatRealtimeService] Error checking participant:', error);
        }
        return;
      }

      if (data) {
        // Refetch all chats to get complete data
        useTeamChatStore.getState().fetchChats();
      }
    } catch (error) {
      console.error('[TeamChatRealtimeService] Error in handleChatInsert:', error);
    }
  }

  /**
   * Gestisce gli stati della sottoscrizione
   */
  private handleSubscriptionStatus(channelName: string, status: string, err?: Error) {
    console.log(`[TeamChatRealtimeService] ${channelName} status:`, status);
    switch (status) {
      case 'SUBSCRIBED':
        if (this.reconnecting) {
          // Dopo reconnect: sincronizza messaggi persi durante la disconnessione
          const store = useTeamChatStore.getState();
          const activeChatId = store.selectedChatId;
          if (activeChatId) {
            store.fetchMessages(activeChatId).catch(() => {});
          }
        }
        this.retryCount = 0;
        this.reconnecting = false;
        break;
      case 'CHANNEL_ERROR':
        console.error(`[TeamChatRealtimeService] ${channelName} error:`, err?.message);
        this.handleConnectionError(err);
        break;
      case 'TIMED_OUT':
        this.handleConnectionError(new Error('Timeout'));
        break;
      case 'CLOSED':
        if (this.isInitialized && !this.reconnecting) {
          this.scheduleReconnect();
        }
        break;
    }
  }

  /**
   * Gestisce errori di connessione con retry exponential backoff
   */
  private handleConnectionError(error: unknown) {
    console.error('[TeamChatRealtimeService] Connection error:', error);
    if (this.retryCount >= this.maxRetries) {
      console.error('[TeamChatRealtimeService] Max tentativi raggiunto');
      return;
    }
    this.scheduleReconnect();
  }

  /**
   * Pianifica riconnessione con exponential backoff
   */
  private scheduleReconnect() {
    if (this.reconnecting || this.retryTimeout) return;

    this.reconnecting = true;
    this.retryCount++;

    const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 16000);

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.connect();
    }, delay);
  }

  /**
   * Disconnette il servizio
   */
  async disconnect() {
    this.isInitialized = false;
    this.reconnecting = false;
    this.userId = null;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    if (this.messagesChannel) {
      await supabase.removeChannel(this.messagesChannel);
      this.messagesChannel = null;
    }

    if (this.chatsChannel) {
      await supabase.removeChannel(this.chatsChannel);
      this.chatsChannel = null;
    }

    if (this.typingChannel) {
      await supabase.removeChannel(this.typingChannel);
      this.typingChannel = null;
    }

    if (this.reactionsChannel) {
      await supabase.removeChannel(this.reactionsChannel);
      this.reactionsChannel = null;
    }
  }
}

// Export singleton instance
export const teamChatRealtimeService = new TeamChatRealtimeService();
